import { ReactElement, useEffect, useState } from 'react';
import { ApiRoute, Route } from 'config';
import { get, post } from 'utils/network';
import Plan from 'models/Plan';
import useAsyncEffect from 'hooks/useAsyncEffect';
import useCurrentUser from 'hooks/useCurrentUser';
import useAddresses from 'hooks/useAddresses';
import useSubscription from 'hooks/useSubscription';
import IPlanInfo from 'models/IPlanInfo';
import { ICoupon } from 'models/Coupon';
import AppliedCoupon from 'models/AppliedCoupon';
import DiversifiedOrderSummary from './DiversifiedOrderSummary';

/* eslint react-hooks/rules-of-hooks: 0 */

type Props = {
  loggedIn?: boolean
  existingPlan: Plan
  menuShippingDate?: string,
};

type gonData = {
  featureFlags?: Record<string, boolean>,
  coupon?: ICoupon
};

function OrderSummary({
  loggedIn = false,
  existingPlan,
  menuShippingDate,
}: Props): ReactElement {
  const [plan, setPlan] = useState(existingPlan);
  // @ts-expect-error TODO: fix null safety
  const appliedCouponData: ICoupon = (window.gon as gonData).coupon ?? null;
  let appliedDiscount: AppliedCoupon;
  let handlePlanChange: (plan: Plan) => void;

  useAsyncEffect(async () => {
    const response = await get<IPlanInfo[]>(ApiRoute.PLANS_SHORT);
    // @ts-expect-error TODO: fix null safety
    Plan.initialize(response.parsedBody);
  }, []);

  if (loggedIn) {
    const { addresses } = useAddresses();
    const { subscription, reload: reloadSubscription } = useSubscription();
    const { currentUser, reload: reloadUser } = useCurrentUser();
    const { signup_summary: signupSummary, coupon } = subscription;

    appliedDiscount = new AppliedCoupon({
      code: signupSummary?.discount?.coupon_name || '',
      allowed_plans: coupon?.allowed_plans || [],
      dollar_off: signupSummary?.discount?.coupon_discount_type === 'dollar_off' ? signupSummary?.discount?.coupon_value : 0,
      free_shipping: subscription?.coupon?.free_shipping || false,
      max_amount: signupSummary?.discount?.value || Infinity,
      max_order_count: 0,
      minimum_base_price: '',
      percentage_off: signupSummary?.discount?.coupon_discount_type === 'percentage_off' ? signupSummary?.discount?.coupon_value : 0,
      status: '',
      extras_only: false,
    });

    handlePlanChange = async (newPlan: Plan) => {
      setPlan(newPlan);

      await post(`${ApiRoute.USERS}/me/subscription`, {
        subscription: {
          plan: newPlan.name,
          extra_meals: newPlan.selectedExtras,
          is_diversified: newPlan.isDiversified,
        },
      });

      reloadUser();
    };

    useEffect(() => {
      const newPlan = new Plan(subscription.plan, subscription.extras, subscription.is_diversified);

      setPlan(newPlan);
    }, [subscription]);

    useAsyncEffect(async () => {
      reloadSubscription();
    }, [currentUser, addresses]);
  } else {
    appliedDiscount = new AppliedCoupon({
      code: appliedCouponData?.discount_code || '',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore -- throws an error on allowed_plans even though it is the correct type
      allowed_plans: appliedCouponData?.allowed_plans || [],
      dollar_off: appliedCouponData?.discount_type === 'dollar_off' ? appliedCouponData?.discount_price : 0,
      free_shipping: appliedCouponData?.constraints?.free_shipping || false,
      max_amount: appliedCouponData?.constraints?.max_amount || Infinity,
      max_order_count: appliedCouponData?.max_order_count || 0,
      minimum_base_price: appliedCouponData?.constraints?.minimum_base_price?.toString() || '',
      percentage_off: appliedCouponData?.discount_type === 'percentage_off' ? appliedCouponData?.discount_price : 0,
      status: '',
      extras_only: appliedCouponData?.constraints?.extras_only || false,
    });

    handlePlanChange = async (newPlan: Plan) => {
      setPlan(newPlan);
    };
  }

  // if we're on Create Account step (logged out), update plan values in signup form hidden inputs
  useEffect(() => {
    if (!loggedIn) {
      const planNameFormInput = document.getElementById('create-account-plan-name') as HTMLInputElement;
      const extrasFormInput = document.getElementById('create-account-extras') as HTMLInputElement;
      const isDiversifiedFormInput = document.getElementById('create-account-is-diversified') as HTMLInputElement;

      planNameFormInput.value = plan.name;
      extrasFormInput.value = plan.extras.toString();
      isDiversifiedFormInput.value = plan.isDiversified.toString();

      const getQueryParams = () => new URLSearchParams(window.location.search);
      const params = getQueryParams();
      params.set('plan', plan.name);
      params.set('diversified', String(plan.isDiversified));
      params.set('extras', String(plan.extras));
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.pushState(null, '', newUrl);
    }
  }, [plan, loggedIn]);

  return (
    <DiversifiedOrderSummary
      selectedPlan={plan}
      onPlanChange={handlePlanChange}
        // @ts-expect-error TODO: fix null safety
      menuShippingDate={menuShippingDate}
      discount={appliedDiscount}
      loggedIn={loggedIn}
    />
  );
}
export default OrderSummary;
